/**
 * The graph window's right-click menu: what can be done with the node, link, group or bundle
 * under the pointer, or with the view when the background was clicked. Pure: it only builds
 * the entries; edits go to the core through `actions` when an entry is chosen.
 */
import type { Graph } from "@protocol/Graph";
import type { Group } from "@protocol/Group";
import type { GraphChange } from "@protocol/GraphChange";
import type { Link } from "@protocol/Link";
import type { Actions } from "../../client/actions";
import { endName } from "../../state/bundles";
import { SEPARATOR, type MenuEntry } from "../../state/menu";
import type { View } from "../../state/view";
import type { WindowManager } from "../../wm/manager";
import { sameCategory } from "../../shared/categories";
import type { KindsService } from "../../state/kinds";
import { toast } from "../../shared/toast";
import type { Target } from "./pointer";
import type { ClipboardEntries } from "./nodeClipboard";

/** What the menu's entries need. */
export interface GraphMenuDeps {
  graph: Graph;
  actions: Actions;
  view: View;
  wm: WindowManager;
  /** The app's general entries (undo, save, windows...), added under the view's. */
  general: () => MenuEntry[];
  /** The graph's categories, in their order. */
  categories: readonly string[];
  /** The kinds that can be picked, and the edit choosing one. */
  kinds: Pick<KindsService, "links" | "ofNode" | "ofLink" | "setLinkKind">;
  /** Zooms around the view's centre. */
  zoom: (factor: number) => void;
  /** Opens the Add node window beside the click, for a node where the click was. */
  addNodeHere: () => void;
  /** Copying nodes and pasting where the click was (left out: not offered). */
  clipboard?: ClipboardEntries;
  /**
   * Given in the class diagram: the boxes selected (a right click on one of several selected
   * boxes offers what can be done with all of them), and "Arrange by group" for the selection
   * (an action, always offered) with its label.
   */
  diagram?: { selected: readonly string[]; arrangeLabel: string; arrange: () => void };
}

/** The entries for a right click on `target`. */
export function graphMenu(deps: GraphMenuDeps, target: Target): MenuEntry[] {
  switch (target.type) {
    case "node":
      if (deps.diagram && deps.diagram.selected.length > 1 && deps.diagram.selected.includes(target.node.id)) return boxesEntries(deps, deps.diagram);
      return nodeEntries(deps, target.node.id);
    case "link":
      return linkEntries(deps, target.link.link);
    case "group":
      return groupEntries(deps, target.group.id);
    case "bundle":
      return [
        { heading: `${endName(deps.graph, target.bundle.from)} → ${endName(deps.graph, target.bundle.to)} · ${target.bundle.links.length} links` },
        { label: "List its links", action: () => inspect(deps, () => deps.view.selectBundle(target.bundle.key)) },
      ];
    case "background":
      return backgroundEntries(deps);
  }
}

/** Selects something and makes sure the inspector is open to show it. */
function inspect(deps: GraphMenuDeps, select: () => void): void {
  select();
  deps.wm.open("inspector");
}

/** A node: inspect, center, link, group, categories, delete. */
function nodeEntries(deps: GraphMenuDeps, id: string): MenuEntry[] {
  const { graph, actions, view } = deps;
  const node = graph.nodes.find((n) => n.id === id);
  if (!node) return [];
  return [
    { heading: `${deps.kinds.ofNode(node).label} · ${node.name}` },
    { label: "Show in the inspector", action: () => inspect(deps, () => view.selectNode(id)) },
    ...(deps.diagram ? [arrangeEntry(deps.diagram)] : []),
    { label: "Center the view on it", action: () => view.requestCenter(id) },
    {
      label: "Draw a link from it",
      action: () => {
        view.selectNode(id);
        view.setLinkTool(true);
        toast(`Drag from ${node.name} to the node it should link to.`);
      },
    },
    {
      label: "Group",
      disabled: graph.groups.length === 0,
      items: [
        { label: "No group", checked: !node.group, action: () => void actions.updateNode(id, { group: "" }) },
        SEPARATOR,
        ...graph.groups.map((g) => ({ label: g.name, checked: node.group === g.id, action: () => void actions.updateNode(id, { group: g.id }) })),
      ],
    },
    {
      label: "Categories",
      // New categories are named in the inspector or the Add node window.
      disabled: deps.categories.length === 0,
      items: deps.categories.map((c) => {
        const has = node.categories.some((x) => sameCategory(x, c));
        return {
          label: c,
          checked: has,
          action: () => {
            void actions.updateNode(id, { categories: has ? node.categories.filter((x) => !sameCategory(x, c)) : [...node.categories, c] });
            if (!has) view.showCategory(c);
          },
        };
      }),
    },
    SEPARATOR,
    ...(deps.clipboard ? [{ label: "Copy", action: () => deps.clipboard!.copy([id]) }] : []),
    { label: "Delete node", danger: true, action: () => void actions.removeNode(id).then((ok) => ok && toast(`${node.name} removed. Press Ctrl+Z to undo.`)) },
  ];
}

/** Several boxes of the class diagram's box selection: arrange them by group, delete them (one undo each). */
function boxesEntries(deps: GraphMenuDeps, diagram: NonNullable<GraphMenuDeps["diagram"]>): MenuEntry[] {
  const ids = [...diagram.selected];
  return [
    { heading: `${ids.length} boxes selected` },
    arrangeEntry(diagram),
    SEPARATOR,
    ...(deps.clipboard ? [{ label: `Copy ${ids.length} nodes`, action: () => deps.clipboard!.copy(ids) }] : []),
    {
      label: `Delete ${ids.length} nodes`,
      danger: true,
      action: () => void deps.actions.removeNodes(ids).then((ok) => ok && (deps.view.selectNode(null), toast(`${ids.length} nodes removed. Press Ctrl+Z to undo.`))),
    },
  ];
}

/** "Arrange by group" for what was right-clicked (selected first), in the class diagram. */
function arrangeEntry(diagram: NonNullable<GraphMenuDeps["diagram"]>): MenuEntry {
  return { label: diagram.arrangeLabel, action: diagram.arrange };
}

/** A link: inspect, change its kind, reverse it, delete it. */
function linkEntries(deps: GraphMenuDeps, link: Link): MenuEntry[] {
  const { graph, actions, view } = deps;
  const name = (id: string) => graph.nodes.find((n) => n.id === id)?.name ?? id;
  /** Keeps the changed link selected (and its kind shown). */
  const follow = (changes: GraphChange[] | null) => {
    const updated = changes?.find((c) => c.type === "link_updated");
    if (updated?.type === "link_updated") {
      view.showLinkKind(updated.after.kind);
      view.selectLink(updated.after);
    }
  };
  /** Applies a change and keeps the link selected. */
  const change = (patch: Parameters<Actions["updateLink"]>[1]) => void actions.updateLink(link, patch).then(follow);
  return [
    { heading: `${name(link.from)} ${deps.kinds.ofLink(link).verb} ${name(link.to)}` },
    { label: "Show in the inspector", action: () => inspect(deps, () => view.selectLink(link)) },
    {
      label: "Kind",
      items: deps.kinds.links().map((k) => ({ label: k.label, checked: link.kind === k.id, action: () => void deps.kinds.setLinkKind(link, k.id).then(follow) })),
    },
    { label: "Reverse its direction", action: () => change({ from: link.to, to: link.from }) },
    SEPARATOR,
    { label: "Delete link", danger: true, action: () => void actions.removeLink(link).then((ok) => ok && toast("Link removed. Press Ctrl+Z to undo.")) },
  ];
}

/** A group: inspect, remove (its nodes stay). */
function groupEntries(deps: GraphMenuDeps, id: string): MenuEntry[] {
  const group = deps.graph.groups.find((g) => g.id === id);
  if (!group) return [];
  return [
    { heading: `Group · ${group.name}` },
    { label: "Show in the inspector", action: () => inspect(deps, () => deps.view.selectGroup(id)) },
    { label: group.collapsed ? "Unfold" : "Fold into one", command: "group.fold", action: () => void deps.actions.updateGroup(id, { collapsed: !group.collapsed }) },
    { label: "Rename…", command: "group.rename", action: () => deps.view.renameGroup(id) },
    ...foldOthersEntry(deps, group),
    ...(deps.diagram ? [arrangeEntry(deps.diagram)] : []),
    SEPARATOR,
    {
      label: "Remove the group (keeps its nodes)",
      danger: true,
      action: () => void deps.actions.removeGroup(id).then((ok) => ok && toast(`Group ${group.name} removed. Press Ctrl+Z to undo.`)),
    },
  ];
}

/**
 * "Fold the other groups": every other open group at the same level (same parent) folded, in
 * one undo, so the group looked at stands out.
 */
function foldOthersEntry(deps: GraphMenuDeps, group: Group): MenuEntry[] {
  const others = deps.graph.groups.filter((g) => g.id !== group.id && (g.parent ?? null) === (group.parent ?? null) && !g.collapsed && (g.environment ?? null) === (group.environment ?? null) && (g.workspace ?? null) === (group.workspace ?? null));
  if (!others.length) return [];
  return [
    {
      label: `Fold the ${others.length} other ${others.length === 1 ? "group" : "groups"} beside it`,
      action: () => void deps.actions.batch(others.map((g) => ({ edit: "update_group" as const, id: g.id, patch: { collapsed: true } }))),
    },
  ];
}

/** The background: add a node, the view, the switches, then the app's general entries. */
function backgroundEntries(deps: GraphMenuDeps): MenuEntry[] {
  const { view } = deps;
  return [
    { label: "Add a node…", command: "node.add", action: deps.addNodeHere },
    ...(deps.clipboard ? [{ label: "Paste here", action: deps.clipboard.pasteHere }] : []),
    SEPARATOR,
    { label: "Fit the view", command: "view.fit" },
    { label: "Zoom in", action: () => deps.zoom(1.25) },
    { label: "Zoom out", action: () => deps.zoom(0.8) },
    SEPARATOR,
    { label: "Link tool", command: "tool.link", checked: view.state.linkTool },
    { label: "Class diagram", command: "view.diagram", checked: !!deps.diagram },
    ...(deps.diagram
      ? [
          arrangeEntry(deps.diagram),
          { label: "Show groups as packages", checked: view.state.groups, action: () => view.setGroups(!view.state.groups) },
          { label: "Bundle links between packages", checked: view.state.bundles, disabled: !view.state.groups, action: () => view.setBundles(!view.state.bundles) },
        ]
      : [
          { label: "3D view", command: "view.3d", checked: view.state.dimension === "3d" },
          { label: "Show labels", checked: view.state.labels, action: () => view.setLabels(!view.state.labels) },
          { label: "Show groups", checked: view.state.groups, action: () => view.setGroups(!view.state.groups) },
          { label: "Bundle links between groups", checked: view.state.bundles, action: () => view.setBundles(!view.state.bundles) },
        ]),
    SEPARATOR,
    ...deps.general(),
  ];
}
